import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Avatar,
  Tooltip,
  Card,
  Typography,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { Link } from "react-router-dom";
import * as MuiIcons from "@mui/icons-material";
import StarIcon from "@mui/icons-material/Star";

const pageCardSx = {
  borderRadius: "18px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
  backgroundColor: "rgba(255,255,255,0.96)",
};

const secondaryButtonSx = {
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 600,
  px: 1.5,
  borderColor: "rgba(47,99,173,0.18)",
  color: "#223142",
  whiteSpace: "nowrap",
  "&:hover": {
    borderColor: "#2f63ad",
    color: "#2f63ad",
    backgroundColor: "rgba(47,99,173,0.04)",
  },
};

const WorkOrderTable = ({ rows = [], getAssignedUserName, getTypeName, getStatusInfo }) => {
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [columnOrder, setColumnOrder] = useState([]);

  useEffect(() => {
    const savedVisibility = localStorage.getItem("workOrderTable_columnVisibility");
    const savedOrder = localStorage.getItem("workOrderTable_columnOrder");

    if (savedVisibility) setColumnVisibilityModel(JSON.parse(savedVisibility));
    if (savedOrder) setColumnOrder(JSON.parse(savedOrder));
  }, []);

  const initialsOf = (full) =>
    (full || "")
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase() || "")
      .join("") || "?";

  const columns = [
    {
      field: "order_id",
      headerName: "Work Order ID",
      width: 150,
      renderCell: (params) =>
        params.value ? (
          <Button
            component={Link}
            to={`${params.value}`}
            state={{ from: "/home/workorders" }}
            variant="text"
            size="small"
            sx={{
              textTransform: "none",
              fontWeight: 700,
              color: "#2f63ad",
              px: 0.5,
              minWidth: "auto",
              "&:hover": {
                backgroundColor: "rgba(47,99,173,0.04)",
              },
            }}
          >
            WO-{params.value}
          </Button>
        ) : (
          "N/A"
        ),
    },
    {
      field: "property_id",
      headerName: "Property ID",
      width: 140,
    },
    {
      field: "assignees_details",
      headerName: "Assigned To",
      width: 280,
      renderCell: (params) => {
        const assignees = (() => {
          if (Array.isArray(params.row?.assignees_details) && params.row.assignees_details.length) {
            return params.row.assignees_details.map((a) => ({
              id: a.user_id,
              name: `${a.name || ""} ${a.lastname || ""}`.trim(),
              isPrimary: !!a.is_primary,
            }));
          }
          if (Array.isArray(params.row?.assignees) && params.row.assignees.length) {
            return params.row.assignees.map((id, idx) => ({
              id,
              name: getAssignedUserName ? getAssignedUserName(id) || `#${id}` : `#${id}`,
              isPrimary: idx === 0,
            }));
          }
          if (params.row?.assign_to != null) {
            const id = params.row.assign_to;
            const name = getAssignedUserName ? getAssignedUserName(id) || `#${id}` : `#${id}`;
            return [{ id, name, isPrimary: true }];
          }
          return [];
        })();

        return assignees.length ? (
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", py: 0.5 }}>
            {assignees.map((a) => (
              <Tooltip key={a.id} title={a.isPrimary ? `${a.name} (Primary)` : a.name}>
                <Chip
                  size="small"
                  variant="outlined"
                  label={a.name}
                  avatar={
                    <Avatar
                      sx={{
                        width: 22,
                        height: 22,
                        fontSize: 11,
                        bgcolor: a.isPrimary ? "primary.main" : "grey.300",
                        color: a.isPrimary ? "primary.contrastText" : "text.primary",
                      }}
                    >
                      {initialsOf(a.name)}
                    </Avatar>
                  }
                  icon={a.isPrimary ? <StarIcon fontSize="small" /> : undefined}
                  sx={{
                    borderRadius: "999px",
                    "& .MuiChip-icon": { ml: 0.25 },
                  }}
                />
              </Tooltip>
            ))}
          </Box>
        ) : (
          <Chip
            label="Not Assigned"
            size="small"
            variant="outlined"
            sx={{ borderRadius: "999px" }}
          />
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 210,
      renderCell: (params) => {
        const statusInfo = getStatusInfo(params.row.status);
        const IconComponent = statusInfo?.icon && MuiIcons[statusInfo.icon];

        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {IconComponent && (
              <IconComponent
                fontSize="small"
                sx={{ color: statusInfo?.color || "#5f6b7a" }}
              />
            )}
            <Chip
              label={statusInfo?.statusname || "Unknown"}
              size="small"
              variant="outlined"
              sx={{
                borderRadius: "999px",
                fontWeight: 600,
                color: "#223142",
                backgroundColor: "rgba(255,255,255,0.96)",
              }}
            />
          </Box>
        );
      },
    },
    {
      field: "full_address",
      headerName: "Address",
      width: 240,
      renderCell: (params) => params.value || "N/A",
    },
    {
      field: "ClubMembershipTypeCode",
      headerName: "Membership Type",
      width: 180,
    },
    {
      field: "priority",
      headerName: "Priority",
      width: 130,
    },
    {
      field: "fullname",
      headerName: "Member Name",
      width: 200,
      renderCell: (params) => {
        const full =
          params.value ||
          `${params.row.FirstName || ""} ${params.row.LastName || ""}`.trim();
        return full || "N/A";
      },
    },
    {
      field: "workorder_type_id",
      headerName: "Type",
      width: 160,
      renderCell: (params) => getTypeName(params.value),
    },
    {
      field: "subtype_name",
      headerName: "Subtype",
      width: 160,
    },
    {
      field: "location_name",
      headerName: "Location",
      width: 160,
    },
    {
      field: "created_at",
      headerName: "Created Date",
      width: 160,
      renderCell: (params) => {
        const raw = params.row.created_at || params.row.createdAt;
        if (!raw) return "N/A";
        const d = new Date(raw);
        return isNaN(d.getTime()) ? "N/A" : d.toLocaleDateString();
      },
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
      minWidth: 250,
      renderCell: (params) => params.value || "N/A",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 140,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Button
          component={Link}
          to={`${params.row.order_id}`}
          state={{ from: "/home/workorders" }}
          variant="outlined"
          size="small"
          disabled={!params.row?.order_id}
          sx={secondaryButtonSx}
        >
          View
        </Button>
      ),
    },
  ];

  const handleVisibilityChange = (newModel) => {
    setColumnVisibilityModel(newModel);
    localStorage.setItem("workOrderTable_columnVisibility", JSON.stringify(newModel));
  };

  const handleColumnOrderChange = (newColumns) => {
    const newOrder = newColumns.map((col) => col.field);
    setColumnOrder(newOrder);
    localStorage.setItem("workOrderTable_columnOrder", JSON.stringify(newOrder));
  };

  return (
 
  
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
        <Box sx={{ width: "100%" }}>
          <DataGrid
            rows={[...rows].sort((a, b) => {
              const da = new Date(a.created_at || a.createdAt);
              const db = new Date(b.created_at || b.createdAt);
              return db - da;
            })}
            columns={columns}
            getRowId={(row) => row.order_id}
            pageSize={10}
            rowsPerPageOptions={[10, 20, 50]}
            disableSelectionOnClick
            autoHeight
            disableColumnReorder={false}
            disableColumnResize={false}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={handleVisibilityChange}
            onColumnOrderChange={handleColumnOrderChange}
            slots={{
              toolbar: GridToolbar,
            }}
            sx={{
              border: "1px solid rgba(47,99,173,0.08)",
              borderRadius: "14px",
              backgroundColor: "#fff",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "rgba(47,99,173,0.04)",
                color: "#223142",
                fontWeight: 700,
                borderBottom: "1px solid rgba(47,99,173,0.08)",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
              },
              "& .MuiDataGrid-row": {
                borderBottom: "1px solid rgba(47,99,173,0.06)",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "rgba(47,99,173,0.03)",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "none",
                alignItems: "center",
              },
              "& .MuiDataGrid-toolbarContainer": {
                padding: "12px 12px 8px 12px",
                borderBottom: "1px solid rgba(47,99,173,0.08)",
                backgroundColor: "rgba(255,255,255,0.9)",
              },
              "& .MuiButton-root": {
                textTransform: "none",
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: "1px solid rgba(47,99,173,0.08)",
                backgroundColor: "rgba(255,255,255,0.92)",
              },
            }}
          />
        </Box>
      </Box>
   
  );
};

export default WorkOrderTable;
